import { useBalanceSolicitation } from "@/contexts/balanceSolicitationContext";
import {
  createBalanceSolicitation,
  getBalanceSolicitationById,
  getLastBalanceSolicitationByDocumentId,
} from "@/services/balanceSolicitation.service";
import { Box, Button, Flex, Text, VStack } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ProgressBar from "@/components/ProgressBar/ProgressBar";
import { getLastProposalByDocumentId } from "@/services/proposal.service";
import { removeNonDigits } from "@/utils/cpfFormatter";
import { useProposalStore } from "@/contexts/useProposalStore";
import { useUserInfo } from "@/contexts/userInfoContext";
import { FaWhatsapp } from "react-icons/fa";
import { keyframes } from "@emotion/react";
import { statusInfoMap } from "@/utils/balanceSolicitationStatusMapper";
import Logo from "@/components/Logo";
import { useSlug } from "@/contexts/selfServiceSlugContext";
import { updateLeadBalanceSolicitation } from "@/services/leadsSelfService.service";

export default function AwaitProposal() {
  const { user } = useUserInfo();
  const { selfServiceSlug } = useSlug();
  const { slug, slugSeller, slugSelfService } = useParams<{
    slug?: string;
    slugSeller?: string;
    slugSelfService?: string;
  }>();
  const { balanceSolicitation, setBalanceSolicitation } =
    useBalanceSolicitation();
  const { proposal, setProposal } = useProposalStore();
  const [statusSuccess, setStatusSuccess] = useState(false);
  const initRef = useRef(false);
  const whatsappNumber = selfServiceSlug?.supportPhone;

  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-2);
  };

  const handleNext = () => {
    navigate(
      slugSelfService
        ? `/${slug}/${slugSeller}/${slugSelfService}/proposal/${balanceSolicitation?.id}`
        : slugSeller
        ? `/${slug}/${slugSeller}/proposal/${balanceSolicitation?.id}`
        : `/${slug}/proposal/${balanceSolicitation?.id}`
    );
  };

  const createBalance = async () => {
    const balance = await createBalanceSolicitation(
      removeNonDigits(user.document),
      selfServiceSlug?.product?.processor?.name!,
      user.name,
      user.phone
    );
    return balance;
  };

  const getLastProposal = async (document: string, slug: string) => {
    try {
      const response = await getLastProposalByDocumentId(document, slug);
      return response;
    } catch (error) {
      return null;
    }
  };

  const getLastBalanceSolicitationByDocument = async (
    document: string,
    selfServiceId: string
  ) => {
    try {
      const response = await getLastBalanceSolicitationByDocumentId(
        document,
        selfServiceId
      );
      return response;
    } catch (error: any) {
      if (error.response?.status === 404) {
        navigate("/404");
      }
      return null;
    }
  };

  useEffect(() => {
    const onInit = async () => {
      if (initRef.current) return;
      initRef.current = true;

      if (user.document && user.document.length >= 11) {
        const proposal = await getLastProposal(
          removeNonDigits(user.document),
          selfServiceSlug?.slug!
        );
        let balance = await getLastBalanceSolicitationByDocument(
          removeNonDigits(user.document),
          selfServiceSlug?.id!
        );

        if (!balance && !proposal) {
          balance = await createBalance();
          await updateLeadBalanceSolicitation(
            selfServiceSlug?.id!,
            balance?.id!,
            balance?.document
          );
        }

        if (balance) setBalanceSolicitation(balance);
        if (proposal) setProposal(proposal);
      } else {
        navigate(
          slugSelfService
            ? `/${slug}/${slugSeller}/${slugSelfService}`
            : slugSeller
            ? `/${slug}/${slugSeller}`
            : `/${slug}`
        );
      }
    };
    onInit();
  }, []);

  useEffect(() => {
    const checkstatus = async () => {
      try {
        const response = await getBalanceSolicitationById(
          balanceSolicitation?.id
        );
        if (response !== null && response?.status !== "PENDING") {
          setBalanceSolicitation(response);
          if (response?.status === "SUCCESS") {
            setStatusSuccess(true);
          } else {
            setStatusSuccess(false);
          }
        }
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
      }
    };

    const interval = setInterval(checkstatus, 2000);
    return () => clearInterval(interval);
  }, [balanceSolicitation, proposal]);

  const handleSupport = () => {
    let phoneNumber = "+55" + whatsappNumber?.replace(/\D/g, "");

    const message =
      "Olá, estou tentando antecipar meu saque aniversário e tenho uma dúvida!";
    const whatsappURL = `https://wa.me/${phoneNumber}/?text=${encodeURIComponent(
      message
    )}`;

    window.open(whatsappURL, "_blank");
  };

  const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

  return (
    <Flex
      py="1rem"
      px={{ base: "1rem", lg: "2rem" }}
      minHeight="100vh"
      w="100%"
      bg="gray.100"
      color="secondary"
      alignItems="center"
      justifyContent="space-between"
      flexDirection="column"
    >
      <Box>
        <Flex justifyContent={"flex-end"} w={"100%"}>
          <Button
            onClick={handleSupport}
            size="sm"
            variant="outline"
            leftIcon={<FaWhatsapp size={16} />}
            color="primary"
            bg="white"
            _hover={{ bg: "white", color: "primary" }}
          >
            Suporte
          </Button>
        </Flex>
        <Text fontSize="sm" textAlign="center" mt={6}>
          <strong>{user.name?.split(" ")[0]}</strong>, aguarde um momento,
          estamos consultando seus dados na{" "}
          <strong>CAIXA ECONÔMICA FEDERAL.</strong>
        </Text>
      </Box>

      <Box
        w={"100%"}
        borderRadius={"25px"}
        bg="white"
        height={"100%"}
        p={6}
        my={"auto"}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="space-around"
        color="secondary"
      >
        <Text fontSize="2xl" fontWeight="bold" mb={6}>
          Consultando na CEF
        </Text>

        <Box w="80px" h="auto" mb={6} animation={`${pulse} 1.5s infinite`}>
          <Logo
            variant="primary"
            alt="Logo"
            style={{ width: "100%", height: "auto" }}
          />
        </Box>

        <VStack spacing={2} mb={8} align="center" textAlign="center">
          {balanceSolicitation?.status && (
            <Text fontWeight="bold">
              {statusInfoMap[balanceSolicitation.status]?.label}
            </Text>
          )}
          {balanceSolicitation?.status &&
            balanceSolicitation.status !== "PENDING" && (
              <Text fontSize="sm">
                {statusInfoMap[balanceSolicitation.status]?.tagLabel}
              </Text>
            )}
        </VStack>

        <Flex flexDirection="column" gap={4} width="100%">
          {(!balanceSolicitation ||
            balanceSolicitation?.status === "PENDING") && <ProgressBar />}
        </Flex>
      </Box>
      <Flex justifyContent="space-between" gap={4} w="100%" mb={12}>
        <Button
          colorScheme="secondary"
          variant="outline"
          w={{ base: "100%", sm: "35%" }}
          fontFamily="Lexend Deca"
          fontSize="sm"
          onClick={handleBack}
        >
          Voltar
        </Button>
        <Button
          bg="primary"
          color="white"
          fontFamily="Lexend Deca"
          fontSize="sm"
          onClick={handleNext}
          isDisabled={!statusSuccess}
          w={{ base: "100%", sm: "35%" }}
        >
          Prosseguir
        </Button>
      </Flex>
    </Flex>
  );
}
